import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { getSupabase, toCurrentUser } from "@/data/supabaseClient";
import type { CurrentUser } from "@/domain/permissions";

export type AuthState =
  | { status: "disabled" }
  | { status: "loading" }
  | { status: "signedOut" }
  /** Arrived through an invite or recovery link and must choose a password. */
  | { status: "setPassword"; session: Session }
  /** Signed in, but `app_metadata.role` is missing or unknown. */
  | { status: "invalidRole" }
  | { status: "signedIn"; user: CurrentUser };

/**
 * Reads the one-time `type` (invite/recovery) Supabase appends to the URL hash.
 * Must run before the Supabase client is created, because it consumes the hash.
 */
function readAuthFlowType(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  return new URLSearchParams(hash).get("type");
}

function stateFromSession(session: Session | null): AuthState {
  if (!session) return { status: "signedOut" };
  const user = toCurrentUser(session);
  return user ? { status: "signedIn", user } : { status: "invalidRole" };
}

export interface AuthSession {
  state: AuthState;
  signIn(email: string, password: string): Promise<boolean>;
  requestPasswordReset(email: string): Promise<void>;
  setPassword(password: string): Promise<boolean>;
  signOut(): Promise<void>;
}

export function useAuthSession(enabled: boolean): AuthSession {
  const [sessionState, setState] = useState<AuthState>({ status: "loading" });
  const state: AuthState = enabled ? sessionState : { status: "disabled" };

  useEffect(() => {
    if (!enabled) return;
    const flowType = readAuthFlowType();
    let cancelled = false;
    void getSupabase()
      .auth.getSession()
      .then(({ data }) => {
        if (cancelled) return;
        const session = data.session;
        if (session && (flowType === "invite" || flowType === "recovery"))
          setState({ status: "setPassword", session });
        else setState(stateFromSession(session));
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await getSupabase().auth.signInWithPassword({ email, password });
    if (error || !data.session) return false;
    setState(stateFromSession(data.session));
    return true;
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await getSupabase().auth.resetPasswordForEmail(email);
  }, []);

  const setPassword = useCallback(
    async (password: string) => {
      if (sessionState.status !== "setPassword") return false;
      const { error } = await getSupabase().auth.updateUser({ password });
      if (error) return false;
      // Drop the one-time tokens so a refresh does not reopen this form.
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );
      setState(stateFromSession(sessionState.session));
      return true;
    },
    [sessionState],
  );

  const signOut = useCallback(async () => {
    await getSupabase().auth.signOut();
    window.location.reload();
  }, []);

  return { state, signIn, requestPasswordReset, setPassword, signOut };
}
